import CheckIcon from '@mui/icons-material/Check'
import ClearIcon from '@mui/icons-material/Clear'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import SearchOffIcon from '@mui/icons-material/SearchOff'
import {
	Box,
	Button,
	ButtonBase,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	InputAdornment,
	Skeleton,
	TextField,
	Typography,
} from '@mui/material'
import { alpha, type Theme } from '@mui/material/styles'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { showErrorToast } from '../../../services/CommonServices/alertServices'
import {
	searchWsrTagNumber,
	formatWsrTagLabel,
	type WsrTagNumberSearchResult,
} from '../../../services/WSRService'

const RECENT_TAG_SELECTIONS_KEY = 'recentTagSelectionsById'
const MAX_RECENT_TAGS = 10
const RANGE_SIZE = 100

type TagRangeGroup = {
	label: string
	tags: WsrTagNumberSearchResult[]
}

type TimeEntrySelectWsrTagDialogProps = {
	open: boolean
	value: string
	onClose: () => void
	onSelect: (tagId: string, tag?: WsrTagNumberSearchResult) => void
	projectOption?: string
}

function readRecentTagSelections(): string[] {
	try {
		const stored = localStorage.getItem(RECENT_TAG_SELECTIONS_KEY)
		if (!stored) return []
		const parsed: unknown = JSON.parse(stored)
		if (!Array.isArray(parsed)) return []
		return parsed
			.filter((item): item is string | number => typeof item === 'string' || typeof item === 'number')
			.map((item) => String(item).trim())
			.filter((item) => item.length > 0)
			.slice(0, MAX_RECENT_TAGS)
	} catch {
		return []
	}
}

function writeRecentTagSelections(tags: string[]): void {
	try {
		localStorage.setItem(RECENT_TAG_SELECTIONS_KEY, JSON.stringify(tags.slice(0, MAX_RECENT_TAGS)))
	} catch {
		// Private browsing / storage disabled — ignore.
	}
}

function pushRecentTagSelection(tagNumber: string): string[] {
	const trimmed = tagNumber.trim()
	if (!trimmed) return readRecentTagSelections()
	const next = [trimmed, ...readRecentTagSelections().filter((tag) => tag !== trimmed)].slice(
		0,
		MAX_RECENT_TAGS,
	)
	writeRecentTagSelections(next)
	return next
}

function getTagRangeStart(tagNumber: number): number {
	return Math.floor(tagNumber / RANGE_SIZE) * RANGE_SIZE
}

function formatTagRangeLabel(rangeStart: number): string {
	return `${rangeStart}-${rangeStart + RANGE_SIZE - 1}`
}

function groupTagsByRange(tags: WsrTagNumberSearchResult[]): TagRangeGroup[] {
	const groups = new Map<number, WsrTagNumberSearchResult[]>()

	for (const tag of tags) {
		const rangeStart = getTagRangeStart(tag.tagNumber)
		const existing = groups.get(rangeStart)
		if (existing) {
			existing.push(tag)
		} else {
			groups.set(rangeStart, [tag])
		}
	}

	return Array.from(groups.entries())
		.sort(([a], [b]) => a - b)
		.map(([rangeStart, rangeTags]) => ({
			label: formatTagRangeLabel(rangeStart),
			tags: rangeTags,
		}))
}

function formatActivityCountLabel(count: number): string {
	return `${count} ${count === 1 ? 'activity' : 'activities'}`
}

const slimScrollbarSx = (theme: Theme) => ({
	scrollbarWidth: 'thin' as const,
	scrollbarColor: `${alpha(theme.palette.grey[500], 0.4)} transparent`,
	'&::-webkit-scrollbar': {
		width: 6,
		height: 6,
	},
	'&::-webkit-scrollbar-track': {
		background: 'transparent',
	},
	'&::-webkit-scrollbar-thumb': {
		backgroundColor: alpha(theme.palette.grey[500], 0.35),
		borderRadius: theme.shape.borderRadius * 2,
	},
	'&::-webkit-scrollbar-thumb:hover': {
		backgroundColor: alpha(theme.palette.grey[500], 0.55),
	},
})

const sectionLabelSx = (theme: Theme) => ({
	color: theme.palette.text.secondary,
	fontWeight: 700,
	letterSpacing: 1,
	lineHeight: 1,
})

const chipWrapSx = {
	display: 'flex',
	flexWrap: 'wrap' as const,
	gap: 1, // 8px
	alignItems: 'center',
}

function tagChipSx(selected: boolean) {
	return (theme: Theme) => {
		const base = {
			minWidth: 48,
			minHeight: 52,
			px: 2.25, // ~18px
			py: 0.75,
			flexShrink: 0,
			borderRadius: 999,
			border: '1px solid',
			display: 'inline-flex',
			flexDirection: 'column' as const,
			alignItems: 'center',
			justifyContent: 'center',
			gap: 0.15,
			WebkitTapHighlightColor: 'transparent',
			transition: theme.transitions.create(
				['background-color', 'border-color', 'color', 'transform', 'box-shadow'],
				{ duration: theme.transitions.duration.shorter },
			),
			'&:focus-visible': {
				outline: `2px solid ${theme.palette.primary.main}`,
				outlineOffset: 2,
			},
		}

		if (selected) {
			return {
				...base,
				color: theme.palette.primary.contrastText,
				backgroundColor: theme.palette.primary.main,
				borderColor: theme.palette.primary.main,
				'@media (hover: hover)': {
					'&:hover': {
						backgroundColor: theme.palette.primary.dark,
						borderColor: theme.palette.primary.dark,
					},
				},
				'&:active': {
					transform: 'scale(0.97)',
					backgroundColor: theme.palette.primary.dark,
				},
			}
		}

		return {
			...base,
			color: theme.palette.text.primary,
			backgroundColor: alpha(theme.palette.grey[500], 0.06),
			borderColor: alpha(theme.palette.grey[600], 0.22),
			'@media (hover: hover)': {
				'&:hover': {
					backgroundColor: alpha(theme.palette.grey[500], 0.14),
					borderColor: alpha(theme.palette.grey[600], 0.32),
				},
			},
			'&:active': {
				transform: 'scale(0.97)',
				backgroundColor: alpha(theme.palette.grey[500], 0.2),
			},
		}
	}
}

function TagChip({
	tag,
	label,
	activityCount,
	selected,
	onSelect,
}: {
	tag: string
	label: string
	activityCount: number
	selected: boolean
	onSelect: (tag: string) => void
}) {
	return (
		<ButtonBase
			onClick={() => onSelect(tag)}
			aria-pressed={selected}
			sx={tagChipSx(selected)}
		>
			<Box
				sx={{
					display: 'inline-flex',
					alignItems: 'center',
					gap: 0.5,
					lineHeight: 1.1,
				}}
			>
				{selected ? <CheckIcon sx={{ fontSize: 16 }} /> : null}
				<Typography
					component='span'
					sx={{
						fontWeight: selected ? 700 : 600,
						fontSize: '0.9375rem',
						fontVariantNumeric: 'tabular-nums',
						letterSpacing: 0.15,
						lineHeight: 1.15,
					}}
				>
					{label}
				</Typography>
			</Box>
			<Typography
				component='span'
				sx={(theme) => ({
					fontSize: '0.6875rem',
					fontWeight: 500,
					lineHeight: 1.2,
					letterSpacing: 0.1,
					color: selected
						? alpha(theme.palette.primary.contrastText, 0.82)
						: theme.palette.text.secondary,
				})}
			>
				{formatActivityCountLabel(activityCount)}
			</Typography>
		</ButtonBase>
	)
}

export default function TimeEntrySelectWsrTagDialog({
	open,
	value,
	onClose,
	onSelect,
	projectOption,
}: TimeEntrySelectWsrTagDialogProps) {
	const [loading, setLoading] = useState(false)
	const [tags, setTags] = useState<WsrTagNumberSearchResult[]>([])
	const [search, setSearch] = useState('')
	const [recentTags, setRecentTags] = useState<string[]>([])

	useEffect(() => {
		if (!open) return

		setSearch('')
		setRecentTags(readRecentTagSelections())
		let cancelled = false

		const load = async () => {
			setLoading(true)
			try {
				const results = await searchWsrTagNumber(projectOption)
				if (!cancelled) {
					setTags(results)
				}
			} catch (error: unknown) {
				if (!cancelled) {
					setTags([])
					const message =
						error && typeof error === 'object' && 'message' in error
							? String((error as { message?: unknown }).message)
							: 'Failed to load tags.'
					showErrorToast({
						message: message || 'Failed to load tags.',
						traceId: '',
						eventId: '',
					})
				}
			} finally {
				if (!cancelled) setLoading(false)
			}
		}

		void load()

		return () => {
			cancelled = true
		}
	}, [open, projectOption])

	const filteredTags = useMemo(() => {
		const trimmed = search.trim()
		if (!trimmed) return tags
		return tags.filter((tag) =>
			formatWsrTagLabel(tag).toLowerCase().includes(trimmed.toLowerCase()),
		)
	}, [tags, search])

	const tagGroups = useMemo(() => groupTagsByRange(filteredTags), [filteredTags])

	const tagById = useMemo(() => {
		const map = new Map<string, WsrTagNumberSearchResult>()
		for (const tag of tags) {
			map.set(String(tag.scaffoldTagId), tag)
		}
		return map
	}, [tags])

	const visibleRecentTags = useMemo(() => {
		if (tags.length === 0) return []
		return recentTags
			.map((tagNumber) => tagById.get(tagNumber))
			.filter((tag): tag is WsrTagNumberSearchResult => tag != null)
	}, [recentTags, tagById, tags.length])

	const handleSelectTag = useCallback(
		(tagNumber: string) => {
			const trimmed = tagNumber.trim()
			if (trimmed) {
				setRecentTags(pushRecentTagSelection(trimmed))
			}
			onSelect(trimmed, tagById.get(trimmed))
		},
		[onSelect, tagById],
	)

	const resultLabel = loading
		? 'Loading tags…'
		: filteredTags.length === tags.length
			? `${filteredTags.length} tag${filteredTags.length === 1 ? '' : 's'}`
			: `${filteredTags.length} of ${tags.length} tags`

	const emptyMessage =
		tags.length === 0 ? 'No tags available for this project.' : 'No tags found matching your search.'

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth='md'
			PaperProps={{
				sx: {
					maxHeight: { xs: '92vh', sm: 'min(760px, 90vh)' },
					display: 'flex',
					flexDirection: 'column',
				},
			}}
		>
			<DialogTitle
				sx={{
					pr: 6,
					pb: 1.5,
					fontWeight: 600,
					letterSpacing: 0.2,
				}}
			>
				Select Tag
			</DialogTitle>
			<IconButton
				onClick={onClose}
				aria-label='Close'
				sx={(theme) => ({
					position: 'absolute',
					right: theme.spacing(1),
					top: theme.spacing(1),
					color: theme.palette.grey[500],
					minWidth: 44,
					minHeight: 44,
				})}
			>
				<CloseIcon />
			</IconButton>

			<DialogContent
				dividers
				sx={{
					pt: 2.5,
					px: { xs: 2, sm: 3 },
					pb: 0,
					display: 'flex',
					flexDirection: 'column',
					gap: 2,
					minHeight: 0,
					flex: 1,
					overflow: 'hidden',
				}}
			>
				<TextField
					autoFocus
					fullWidth
					size='medium'
					placeholder='Search tag number…'
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					InputProps={{
						startAdornment: (
							<InputAdornment position='start'>
								<SearchIcon
									fontSize='small'
									sx={(theme) => ({ color: theme.palette.text.secondary })}
								/>
							</InputAdornment>
						),
						endAdornment: search ? (
							<InputAdornment position='end'>
								<IconButton
									size='small'
									aria-label='Clear search'
									onClick={() => setSearch('')}
									edge='end'
									sx={{ minWidth: 40, minHeight: 40 }}
								>
									<ClearIcon fontSize='small' />
								</IconButton>
							</InputAdornment>
						) : undefined,
					}}
					sx={(theme) => ({
						flexShrink: 0,
						'& .MuiOutlinedInput-root': {
							borderRadius: 999,
							backgroundColor: alpha(theme.palette.grey[500], 0.08),
							'& fieldset': {
								borderColor: 'transparent',
							},
							'&:hover': {
								backgroundColor: alpha(theme.palette.grey[500], 0.12),
								'& fieldset': {
									borderColor: 'transparent',
								},
							},
							'&.Mui-focused': {
								backgroundColor: alpha(theme.palette.grey[500], 0.06),
								'& fieldset': {
									borderColor: theme.palette.primary.main,
									borderWidth: 1.5,
								},
								boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.14)}`,
							},
						},
					})}
				/>

				{/* Recent */}
				<Box sx={{ flexShrink: 0 }}>
					<Typography variant='overline' sx={(theme) => ({ ...sectionLabelSx(theme), mb: 1.25 })}>
						Recent
					</Typography>
					{visibleRecentTags.length === 0 ? (
						<Typography
							variant='body2'
							color='text.secondary'
							sx={{ px: 0.25, minHeight: 40, display: 'flex', alignItems: 'center' }}
						>
							No recent tags yet
						</Typography>
					) : (
						<Box
							sx={(theme) => ({
								display: 'flex',
								flexWrap: 'nowrap',
								gap: 1,
								overflowX: 'auto',
								pb: 0.5,
								WebkitOverflowScrolling: 'touch',
								...slimScrollbarSx(theme),
							})}
						>
							{visibleRecentTags.map((tag) => (
								<TagChip
									key={tag.scaffoldTagId}
									tag={String(tag.scaffoldTagId)}
									label={formatWsrTagLabel(tag)}
									activityCount={tag.activityCount}
									selected={String(tag.scaffoldTagId) === value}
									onSelect={handleSelectTag}
								/>
							))}
						</Box>
					)}
				</Box>

				{/* All tags header + scrollable chip list fills remaining height */}
				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						display: 'flex',
						flexDirection: 'column',
					}}
				>
					<Box
						sx={(theme) => ({
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							gap: 1,
							flexShrink: 0,
							pb: 1.25,
							mb: 1.5,
							borderBottom: `1px solid ${alpha(theme.palette.grey[600], 0.2)}`,
						})}
					>
						<Typography variant='overline' sx={sectionLabelSx}>
							All Tags
						</Typography>
						<Typography variant='caption' color='text.secondary' sx={{ letterSpacing: 0.3 }}>
							{resultLabel}
						</Typography>
					</Box>

					<Box
						sx={(theme) => ({
							flex: 1,
							minHeight: 0,
							overflowY: 'auto',
							WebkitOverflowScrolling: 'touch',
							pr: 0.5,
							pb: 1,
							...slimScrollbarSx(theme),
						})}
					>
						{loading ? (
							<Box sx={chipWrapSx}>
								{Array.from({ length: 14 }).map((_, index) => (
									<Skeleton
										key={index}
										variant='rounded'
										width={72 + (index % 4) * 16}
										height={52}
										sx={{ borderRadius: 999 }}
									/>
								))}
							</Box>
						) : filteredTags.length === 0 ? (
							<Box
								sx={{
									minHeight: 200,
									height: '100%',
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									justifyContent: 'center',
									gap: 1,
									px: 3,
									py: 4,
									textAlign: 'center',
								}}
							>
								<SearchOffIcon
									sx={(theme) => ({
										fontSize: 40,
										color: alpha(theme.palette.text.secondary, 0.45),
									})}
								/>
								<Typography variant='subtitle2' color='text.secondary'>
									No tags found
								</Typography>
								<Typography variant='body2' color='text.secondary'>
									{emptyMessage}
								</Typography>
							</Box>
						) : (
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
								{tagGroups.map((group) => (
									<Box key={group.label}>
										<Typography
											variant='subtitle2'
											sx={(theme) => ({
												mb: 1.25,
												color: theme.palette.text.secondary,
												fontWeight: 600,
												letterSpacing: 0.2,
											})}
										>
											{group.label}
										</Typography>
										<Box sx={chipWrapSx}>
											{group.tags.map((tag) => {
												const tagId = String(tag.scaffoldTagId)
												return (
													<TagChip
														key={tag.scaffoldTagId}
														tag={tagId}
														label={formatWsrTagLabel(tag)}
														activityCount={tag.activityCount}
														selected={tagId === value}
														onSelect={handleSelectTag}
													/>
												)
											})}
										</Box>
									</Box>
								))}
							</Box>
						)}
					</Box>
				</Box>
			</DialogContent>

			<DialogActions
				sx={(theme) => ({
					px: { xs: 2, sm: 3 },
					py: 1.75,
					justifyContent: 'space-between',
					gap: 1,
					borderTop: `1px solid ${alpha(theme.palette.grey[600], 0.16)}`,
				})}
			>
				{value ? (
					<Button
						color='inherit'
						onClick={() => onSelect('')}
						sx={{ minHeight: 40, px: 1.5 }}
					>
						Clear Selection
					</Button>
				) : (
					<Box />
				)}
				<Button onClick={onClose} sx={{ minHeight: 40, px: 2.5 }}>
					Cancel
				</Button>
			</DialogActions>
		</Dialog>
	)
}
